import React from 'react'
import { twMerge } from 'tailwind-merge'
import { XCircleFillIcon } from '../../icons/x-circle-fill-icon'

export enum BadgeSizeEnum {
  Small = 'small',
  Medium = 'medium',
  Large = 'large',
}

export enum BadgeShapeEnum {
  Rounded = 'rounded',
  Fill = 'fill',
}

export enum BadgeColorEnum {
  Gray = 'gray',
  Primary = 'primary',
}

export interface BadgeProps extends React.HTMLAttributes<HTMLDivElement> {
  size?: BadgeSizeEnum
  shape?: BadgeShapeEnum
  color?: BadgeColorEnum
  withCircle?: boolean
  children: React.ReactNode
  closeButtonProps?: React.HTMLAttributes<SVGSVGElement>
  withoutCloseButton?: boolean
}

const getSizeClasses = (size: BadgeSizeEnum) => {
  const sizes = {
    [BadgeSizeEnum.Small]: 'px-1.5 py-0.5 text-S leading-4',
    [BadgeSizeEnum.Medium]: 'px-2.5 py-1.5 text-S leading-4',
    [BadgeSizeEnum.Large]: 'px-3 py-1.5 text-M',
  }
  return sizes[size]
}

const getShapeClasses = (shape: BadgeShapeEnum) => {
  const shapes = {
    [BadgeShapeEnum.Fill]: 'rounded-full',
    [BadgeShapeEnum.Rounded]: 'rounded-md',
  }
  return shapes[shape]
}

const getColorClasses = (color: BadgeColorEnum) => {
  const colors = {
    [BadgeColorEnum.Gray]: 'bg-tagSoftGrayBg text-tagSoftGrayText',
    [BadgeColorEnum.Primary]: 'bg-tagSoftPrimaryBg text-tagSoftPrimaryText',
  }
  return colors[color]
}

const getCircleColorClasses = (color: BadgeColorEnum) => {
  const colors = {
    [BadgeColorEnum.Gray]: 'bg-gray-500',
    [BadgeColorEnum.Primary]: 'bg-primary-600',
  }
  return colors[color]
}

const getCloseButtonClasses = (color: BadgeColorEnum) => {
  const colors = {
    [BadgeColorEnum.Gray]:
      '[&>path:first-child]:fill-gray-150 [&>path:last-child]:fill-iconSecondary',
    [BadgeColorEnum.Primary]:
      '[&>path:first-child]:fill-primary-200 [&>path:last-child]:fill-tagSoftPrimaryIcon',
  }
  return colors[color]
}

// TODO: extend it https://www.figma.com/design/bGbln5WpRxSxWaY8I4MLkX/Systeme.io-Design-System?node-id=3139-4329&m=dev
export const Badge = ({
  children,
  className,
  size = BadgeSizeEnum.Medium,
  shape = BadgeShapeEnum.Rounded,
  color = BadgeColorEnum.Gray,
  withCircle,
  withoutCloseButton,
  closeButtonProps,
  ...props
}: BadgeProps) => {
  return (
    <div
      className={twMerge(
        'flex w-fit items-center gap-1.5',
        getSizeClasses(size),
        getShapeClasses(shape),
        getColorClasses(color),
        className,
      )}
      {...props}
    >
      {withCircle && (
        <div
          className={twMerge(
            'h-1.5 w-1.5 rounded-full',
            getCircleColorClasses(color),
          )}
        />
      )}
      <span>{children}</span>
      {closeButtonProps && !withoutCloseButton && (
        <XCircleFillIcon
          className={twMerge(
            'h-4 w-4 cursor-pointer',
            getCloseButtonClasses(color),
          )}
          {...closeButtonProps}
        />
      )}
    </div>
  )
}
